import SettingsIcon from 'assets/images/settings-icon.svg';
import AgentsIcon from 'assets/images/agent-icon.svg';
import ApiKeyIcon from 'assets/images/api-key-icon.svg';
import CredentialsIcon from 'assets/images/credentials-icon.svg';
import NotificationsIcon from 'assets/images/notification-icon.svg';
import PoliciesIcon from 'assets/images/policies-icon.svg';
import KeysIcon from 'assets/images/key-icon.svg';
import BillingIcon from 'assets/images/billing-icon.svg';
import IntegrationsIcon from 'assets/images/integrations-icon.svg';
import TeamsIcon from 'assets/images/team-icon.svg';
import RolesIcon from 'assets/images/roles-icon.svg';
import UsersIcon from 'assets/images/user-icon.svg';
import AuditLogsIcon from 'assets/images/audit-logs.svg';
import VcsIcon from 'assets/images/git-self-hosted.svg';
import SsoIcon from 'assets/images/sso-icon.svg';
import { useNavigate } from 'react-router';
import type { Action } from 'kbar';
import type { OrganizationSettingsTabName } from 'constants/organization-settings.constants';
import { ORGANIZATION_SETTINGS_TABS } from 'constants/organization-settings.constants';
import { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useHasPermission } from 'hooks/use-has-permission';
import { useCurrentOrganizationId } from 'hooks/use-current-organization-id';

export const ORGANIZATION_SETTINGS_ROOT_ID = 'SETTINGS_SECTION';

const section = 'Organization Settings';

const tabToIcon: Record<OrganizationSettingsTabName, string> = {
  general: SettingsIcon,
  users: UsersIcon,
  teams: TeamsIcon,
  roles: RolesIcon,
  'api-keys': ApiKeyIcon,
  credentials: CredentialsIcon,
  keys: KeysIcon,
  policy: PoliciesIcon,
  'audit-logs': AuditLogsIcon,
  notifications: NotificationsIcon,
  agents: AgentsIcon,
  integrations: IntegrationsIcon,
  billing: BillingIcon,
  vcs: VcsIcon,
  sso: SsoIcon,
  migration: SettingsIcon
};

const useOrganizationSettingsTabsActions = () => {
  const navigate = useNavigate();
  const intl = useIntl();
  const { isAuthorized: canEditOrgSettings } = useHasPermission('EDIT_ORGANIZATION_SETTINGS');
  const { isAuthorized: canManageBilling } = useHasPermission('MANAGE_BILLING');
  const { isAuthorized: canCreateCustomRoles } = useHasPermission('CREATE_CUSTOM_ROLES');
  const { isAuthorized: canAccessVcsConnections } = useHasPermission('MANAGE_VCS_CONNECTIONS');
  const currentOrganizationId = useCurrentOrganizationId();

  const tabActions: Action[] = useMemo(() => {
    if (canEditOrgSettings) {
      return ORGANIZATION_SETTINGS_TABS.map(tabName => ({
        id: `SETTINGS_TAB_${tabName}`,
        parent: ORGANIZATION_SETTINGS_ROOT_ID,
        name: intl.formatMessage({ id: `organization.settings.menu.${tabName}` }),
        section,
        perform: () => navigate(`/organizations/${currentOrganizationId}/${tabName}`),
        icon: tabToIcon[tabName]
      }));
    }

    const actions: Action[] = [];

    if (canAccessVcsConnections) {
      actions.push({
        id: 'SETTINGS_TAB_vcs',
        name: intl.formatMessage({ id: 'organization.settings.menu.vcs' }),
        section,
        perform: () => navigate(`/organizations/${currentOrganizationId}/vcs`),
        icon: tabToIcon.vcs
      });
    }

    if (canManageBilling) {
      actions.push({
        id: 'SETTINGS_TAB_billing',
        name: intl.formatMessage({ id: 'organization.settings.menu.billing' }),
        section,
        perform: () => navigate(`/organizations/${currentOrganizationId}/billing`),
        icon: tabToIcon.billing
      });
    }

    if (canCreateCustomRoles) {
      actions.push({
        id: 'SETTINGS_TAB_roles',
        name: intl.formatMessage({ id: 'organization.settings.menu.roles' }),
        section,
        perform: () => navigate(`/organizations/${currentOrganizationId}/roles`),
        icon: tabToIcon.roles
      });
    }

    return actions;
  }, [
    canEditOrgSettings,
    canAccessVcsConnections,
    canManageBilling,
    canCreateCustomRoles,
    intl,
    navigate,
    currentOrganizationId
  ]);

  return tabActions;
};

export default useOrganizationSettingsTabsActions;
